import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getIsActiveTabFromParams } from 'utils/content/getIsActiveTabFromParams';
import { getTabLinkFromParams } from 'utils/content/getTabLinkFromParams';
import Button from 'components/Button';
import Card from 'components/Card';
import Modal from 'components/Modal';
import ModalHeadline from 'components/ModalHeadline';
import StickyButtonPanel from 'components/StickyButtonPanel';
import SinglePlaylistManager from 'screens/SinglePlaylistManager';
import ConfirmationModal from './ConfirmationModal';
import PlaylistEditor from './PlaylistEditor';
import { usePlaylistForm } from './hooks/usePlaylistForm';
import { formatPlaylistSubmissionData } from './utils/formatPlaylistSubmissionData';
import { hasPrivilege, PLAYLIST_WRITE } from 'utils/privileges';
import { useFetchUserProfile } from 'actions/userProfile';
import EmptyStateController from 'components/EmptyStateController';
import { StickyEditorPageHeaderWrapper } from 'components/EditorPageHeader';
function PlaylistEditorModalWithData(_ref) {
  var _ref$inContentManager = _ref.inContentManagerContext,
    inContentManagerContext = _ref$inContentManager === void 0 ? false : _ref$inContentManager,
    playlist = _ref.playlist,
    initialOwner = _ref.initialOwner,
    onSubmit = _ref.onSubmit,
    onClose = _ref.onClose;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var titleId = 'playlist-editor-dialog-title';
  var isEditMode = !!playlist;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isConfirmationModalOpen = _useState2[0],
    setConfirmationModalOpen = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isSubmissionLoading = _useState4[0],
    setIsSubmissionLoading = _useState4[1];
  var playlistForm = usePlaylistForm({
    playlist: playlist,
    initialOwner: initialOwner
  });
  var handleSubmit = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(e) {
      var cleanedPlaylistData;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            cleanedPlaylistData = formatPlaylistSubmissionData({
              playlistForm: playlistForm,
              isEditMode: isEditMode
            });
            e.preventDefault();
            _context.prev = 2;
            setIsSubmissionLoading(true);
            _context.next = 6;
            return onSubmit(cleanedPlaylistData);
          case 6:
            _context.next = 11;
            break;
          case 8:
            _context.prev = 8;
            _context.t0 = _context["catch"](2);
            setIsSubmissionLoading(false);
          case 11:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[2, 8]]);
    }));
    return function handleSubmit(_x) {
      return _ref2.apply(this, arguments);
    };
  }();
  var handleClose = function handleClose() {
    if (playlistForm.wasChanged) {
      setConfirmationModalOpen(true);
    } else {
      onClose();
    }
  };
  if (isEditMode) {
    return /*#__PURE__*/React.createElement(Modal, {
      "aria-labelledby": titleId,
      onRequestClose: onClose,
      layout: "top-aligned",
      width: "extraWide"
    }, /*#__PURE__*/React.createElement(SinglePlaylistManager, {
      inContentManagerContext: inContentManagerContext,
      titleId: titleId,
      playlistId: playlist === null || playlist === void 0 ? void 0 : playlist.id,
      playlist: playlist,
      enableNextSteps: false,
      showHeaderBackLink: false,
      showContentTab: false,
      showAccessTab: false,
      showShareTab: false,
      hasDeleteOrRestoreOption: false,
      hasDuplicateOption: false,
      getIsActiveTab: getIsActiveTabFromParams,
      getTabLink: getTabLinkFromParams,
      onClose: handleClose,
      onSubmitDetailChanges: onSubmit
    }));
  }
  return /*#__PURE__*/React.createElement(Modal, {
    id: "playlist-editor",
    width: "extraWide",
    "aria-labelledby": titleId,
    onRequestClose: handleClose
  }, /*#__PURE__*/React.createElement(StickyEditorPageHeaderWrapper, null, /*#__PURE__*/React.createElement(Card, {
    variant: "raised"
  }, /*#__PURE__*/React.createElement(ModalHeadline, {
    sticky: true,
    title: t('actions_add_playlist'),
    titleId: titleId,
    onClose: onClose
  }))), /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit
  }, /*#__PURE__*/React.createElement(PlaylistEditor, {
    playlist: playlist,
    playlistForm: playlistForm,
    withParentPlaylistsInfo: true
  }), isConfirmationModalOpen && /*#__PURE__*/React.createElement(ConfirmationModal, {
    onConfirm: onClose,
    onCancel: function onCancel() {
      return setConfirmationModalOpen(false);
    },
    editMode: isEditMode
  }), /*#__PURE__*/React.createElement(StickyButtonPanel, {
    mt: "m",
    p: "0"
  }, /*#__PURE__*/React.createElement(Card, {
    variant: "raised",
    p: "m"
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "primary",
    isDisabled: playlistForm.hasErrors || !playlistForm.wasChanged,
    isLoading: isSubmissionLoading
  }, t('actions_save_changes'))))));
}
function PlaylistEditorModal(props) {
  var _props$playlist;
  var isEditMode = !!(props !== null && props !== void 0 && (_props$playlist = props.playlist) !== null && _props$playlist !== void 0 && _props$playlist.id);
  var isPlaylistAdmin = hasPrivilege(PLAYLIST_WRITE);
  var _useFetchUserProfile = useFetchUserProfile({
      enabled: !isEditMode && isPlaylistAdmin
    }),
    userProfile = _useFetchUserProfile.data,
    isLoading = _useFetchUserProfile.isLoading;
  var initialOwner = userProfile && {
    id: userProfile.id,
    displayName: userProfile.displayName,
    isDeleted: false
  };
  return /*#__PURE__*/React.createElement(EmptyStateController, {
    isLoading: isLoading,
    height: 600
  }, !isLoading && /*#__PURE__*/React.createElement(PlaylistEditorModalWithData, _extends({
    initialOwner: initialOwner
  }, props)));
}
export { PlaylistEditor } from './PlaylistEditor';
export { useSubmitPlaylistEditor } from './hooks/useSubmitPlaylistEditor';
export { useSubmitPlaylistCreator } from './hooks/useSubmitPlaylistCreator';
export { formatPlaylistEditorPayloadForApi } from './utils/formatPlaylistEditorPayloadForApi';
export { formatPlaylistSubmissionData } from './utils/formatPlaylistSubmissionData';
export { usePlaylistForm } from './hooks/usePlaylistForm';
export default PlaylistEditorModal;